// Entrega de pedidos RETIRA: el cliente retira en depósito.
// Muestra todos los pedidos con metodoEntrega=="RETIRA" del depósito.
// - DESPACHADOS (en pedidos_despachados) → escanear bultos → ENTREGADO
// - Activos (en pedidos) → informativo / estado actual

import React, { useState, useEffect, useCallback } from "react";
import {
  collection, query, where, onSnapshot,
  doc, updateDoc, serverTimestamp,
} from "firebase/firestore";
import { db } from "../../../firebase";
import { useAuth } from "../../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import QrScanner from "../../../components/QrScanner";

function fmtHora(ts) {
  if (!ts?.toDate) return "—";
  return ts.toDate().toLocaleTimeString("es-UY", { hour: "2-digit", minute: "2-digit" });
}

function estadoLabel(estado) {
  const map = {
    PENDIENTE_ASIGNAR: "Pendiente asignar",
    ASIGNADO: "Asignado",
    EN_PREPARACION: "En preparación",
    CON_ERROR: "Con error",
    PREPARADO: "Preparado",
    CONTROLADO: "Controlado",
    DESPACHADO: "Despachado",
  };
  return map[estado] || estado?.replace(/_/g, " ") || "—";
}

// ── Scanner modal ─────────────────────────────────────────────
function ScanModal({ pedido, onClose, onEntregado }) {
  const totalBultos = Number(pedido.bultos) || 1;
  const [escaneados, setEscaneados] = useState(new Set());
  const [error, setError]           = useState("");
  const [saving, setSaving]         = useState(false);

  const handleScan = useCallback((raw) => {
    setError("");
    // QR format: "${pedidoId}|${bultoN}/${totalBultos}"
    const texto = typeof raw === "string" ? raw : raw?.text || raw?.decodedText || String(raw);
    const match = texto.match(/^(.+)\|(\d+)\/(\d+)$/);
    if (!match) {
      setError(`QR no reconocido: "${texto.slice(0, 40)}"`);
      return;
    }
    const [, pid, bultoN, total] = match;
    if (pid !== pedido.id) {
      setError(`Este bulto es de otro pedido (#${pid})`);
      return;
    }
    const n = Number(bultoN);
    if (escaneados.has(n)) {
      setError(`Bulto ${n}/${total} ya escaneado`);
      return;
    }
    setEscaneados(prev => new Set([...prev, n]));
  }, [pedido.id, escaneados]);

  const todosEscaneados = escaneados.size === totalBultos;

  async function confirmarEntrega() {
    setSaving(true);
    try {
      await updateDoc(doc(db, "pedidos_despachados", pedido.id), {
        cargaEstado: "ENTREGADO",
        entregadoAt: serverTimestamp(),
        bultosEscaneados: [...escaneados].sort((a, b) => a - b),
      });
      onEntregado();
    } catch (e) {
      setError("Error al guardar: " + (e?.message || e));
      setSaving(false);
    }
  }

  return (
    <div
      onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 1000, display: "flex", alignItems: "flex-end" }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: "#fff", width: "100%",
          borderRadius: "16px 16px 0 0", padding: "20px 20px 32px",
          maxHeight: "90vh", overflowY: "auto",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
          <div>
            <h3 style={{ margin: 0, fontSize: "16px", fontWeight: 700 }}>
              📷 Escanear bultos — #{pedido.numero}
            </h3>
            <p style={{ margin: "3px 0 0", fontSize: "13px", color: "#64748b" }}>{pedido.cliente}</p>
          </div>
          <button
            onClick={onClose}
            style={{ background: "none", border: "none", fontSize: "24px", cursor: "pointer", color: "#94a3b8", lineHeight: 1 }}
          >×</button>
        </div>

        {/* Progreso de bultos */}
        <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", marginBottom: "14px" }}>
          {Array.from({ length: totalBultos }, (_, i) => i + 1).map(n => (
            <div key={n} style={{
              width: "36px", height: "36px",
              borderRadius: "8px",
              background: escaneados.has(n) ? "#16a34a" : "#f1f5f9",
              color: escaneados.has(n) ? "#fff" : "#94a3b8",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontWeight: 700, fontSize: "14px",
              border: "1.5px solid",
              borderColor: escaneados.has(n) ? "#16a34a" : "#e2e8f0",
            }}>
              {escaneados.has(n) ? "✓" : n}
            </div>
          ))}
        </div>
        <p style={{ margin: "0 0 12px", fontSize: "13px", color: "#64748b" }}>
          {escaneados.size}/{totalBultos} bultos escaneados
        </p>

        {error && (
          <div style={{
            background: "#fee2e2", border: "1px solid #fca5a5",
            borderRadius: "8px", padding: "10px 14px",
            color: "#dc2626", fontSize: "13px", fontWeight: 600,
            marginBottom: "12px",
          }}>
            ⚠️ {error}
          </div>
        )}

        {!todosEscaneados && (
          <QrScanner
            onScanSuccess={handleScan}
            qrbox={{ width: 240, height: 240 }}
          />
        )}

        {todosEscaneados && (
          <div style={{
            background: "#f0fdf4", border: "1.5px solid #86efac",
            borderRadius: "12px", padding: "14px 16px", marginBottom: "16px",
            textAlign: "center",
          }}>
            <div style={{ fontSize: "28px", marginBottom: "6px" }}>✅</div>
            <p style={{ margin: 0, fontWeight: 700, color: "#166534", fontSize: "15px" }}>
              Todos los bultos escaneados
            </p>
          </div>
        )}

        <button
          onClick={confirmarEntrega}
          disabled={!todosEscaneados || saving}
          style={{
            width: "100%", padding: "15px", borderRadius: "12px", border: "none",
            fontWeight: 700, fontSize: "15px",
            background: todosEscaneados && !saving ? "#0f172a" : "#e2e8f0",
            color: todosEscaneados && !saving ? "#fff" : "#94a3b8",
            cursor: todosEscaneados && !saving ? "pointer" : "not-allowed",
            marginTop: "8px",
          }}
        >
          {saving ? "Confirmando…" : "✓ Confirmar entrega"}
        </button>
      </div>
    </div>
  );
}

// ── Vista principal ────────────────────────────────────────────
export default function EntregaRetira() {
  const { profile } = useAuth();
  const navigate    = useNavigate();
  const deposito    = profile?.deposito || null;

  const [activos,     setActivos]     = useState([]);
  const [despachados, setDespachados] = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [scanPedido,  setScanPedido]  = useState(null); // pedido que se está escaneando
  const [toast,       setToast]       = useState(null);

  const showToast = (msg, tipo = "ok") => {
    setToast({ msg, tipo });
    setTimeout(() => setToast(null), 3000);
  };

  // Pedidos activos RETIRA
  useEffect(() => {
    if (!deposito) { setLoading(false); return; }
    const q = query(
      collection(db, "pedidos"),
      where("deposito", "==", deposito),
      where("metodoEntrega", "==", "RETIRA"),
    );
    const unsub = onSnapshot(q, snap => {
      setActivos(snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(p => p.estado !== "ANULADO"));
      setLoading(false);
    });
    return () => unsub();
  }, [deposito]);

  // Pedidos despachados RETIRA (ya con ventas despacho)
  useEffect(() => {
    if (!deposito) return;
    const q = query(
      collection(db, "pedidos_despachados"),
      where("deposito", "==", deposito),
      where("metodoEntrega", "==", "RETIRA"),
    );
    const unsub = onSnapshot(q, snap => {
      setDespachados(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, [deposito]);

  const paraEntregar  = despachados.filter(p => p.cargaEstado !== "ENTREGADO");
  const yaEntregados  = despachados.filter(p => p.cargaEstado === "ENTREGADO");

  return (
    <div className="container" style={{ paddingBottom: "40px" }}>

      {toast && (
        <div className="toast" style={{
          background: toast.tipo === "ok" ? "var(--ok)" : "var(--error)",
        }}>
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <header className="topbar card" style={{ marginBottom: "16px" }}>
        <button onClick={() => navigate("/encargado/cola-despacho")} className="btn btn--ghost btn-sm">
          ⬅ Volver
        </button>
        <div>
          <h1 className="h1" style={{ margin: 0, fontSize: "17px" }}>🏪 Retira</h1>
          <p style={{ margin: 0, fontSize: "12px", color: "#64748b" }}>
            Clientes que retiran en depósito
          </p>
        </div>
        <div style={{ display: "flex", gap: "6px" }}>
          {paraEntregar.length > 0 && (
            <span className="pill pill--warn">{paraEntregar.length} para entregar</span>
          )}
          {activos.length > 0 && (
            <span className="pill" style={{ background: "#eff6ff", color: "#1d4ed8" }}>
              {activos.length} en prep.
            </span>
          )}
        </div>
      </header>

      {loading ? (
        <div style={{ textAlign: "center", padding: "60px", color: "#94a3b8" }}>Cargando…</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>

          {/* ── Para entregar (DESPACHADOS, aún no entregados) ── */}
          {paraEntregar.length > 0 && (
            <div className="card" style={{ padding: 0, overflow: "hidden" }}>
              <div style={{
                padding: "12px 16px",
                background: "#fefce8", borderBottom: "1px solid #fde68a",
                fontWeight: 700, fontSize: "14px", color: "#92400e",
              }}>
                📦 Listos para entregar ({paraEntregar.length})
              </div>
              <div style={{ padding: "12px", display: "flex", flexDirection: "column", gap: "8px" }}>
                {paraEntregar.map(p => (
                  <div key={p.id} style={{
                    background: "#fff", border: "1.5px solid #e2e8f0",
                    borderRadius: "12px", padding: "14px 16px",
                    display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px",
                  }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: "14px", color: "#0f172a" }}>
                        #{p.numero || p.id}
                      </div>
                      <div style={{ fontSize: "13px", color: "#334155", marginTop: "2px" }}>{p.cliente}</div>
                      <div style={{ fontSize: "12px", color: "#94a3b8", marginTop: "2px" }}>
                        {p.despachadoAt && `Despacho: ${fmtHora(p.despachadoAt)} · `}
                        📫 {p.bultos ?? "—"} bultos
                      </div>
                    </div>
                    <button
                      onClick={() => setScanPedido(p)}
                      style={{
                        padding: "10px 16px", borderRadius: "10px", border: "none",
                        background: "#0f172a", color: "#fff",
                        fontWeight: 700, fontSize: "13px", cursor: "pointer",
                        flexShrink: 0,
                      }}
                    >
                      📷 Escanear
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── En preparación (pedidos activos) ── */}
          {activos.length > 0 && (
            <div className="card" style={{ padding: 0, overflow: "hidden" }}>
              <div style={{
                padding: "12px 16px",
                background: "#f8fafc", borderBottom: "1px solid #e2e8f0",
                fontWeight: 700, fontSize: "14px", color: "#475569",
              }}>
                🕐 En preparación ({activos.length})
              </div>
              <div style={{ padding: "10px 12px 12px", display: "flex", flexDirection: "column", gap: "6px" }}>
                {activos.map(p => (
                  <div key={p.id} style={{
                    background: "#f8fafc", border: "1.5px solid #e2e8f0",
                    borderRadius: "10px", padding: "11px 14px",
                    display: "flex", justifyContent: "space-between", alignItems: "center",
                    opacity: 0.85,
                  }}>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: "13px", color: "#334155" }}>
                        #{p.numero || p.id}
                      </div>
                      <div style={{ fontSize: "12px", color: "#64748b", marginTop: "2px" }}>{p.cliente}</div>
                    </div>
                    <span style={{
                      fontSize: "11px", fontWeight: 600,
                      background: "#f1f5f9", color: "#64748b",
                      padding: "4px 10px", borderRadius: "20px",
                    }}>
                      {estadoLabel(p.estado)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Ya entregados (historial del día) ── */}
          {yaEntregados.length > 0 && (
            <div className="card" style={{ padding: 0, overflow: "hidden" }}>
              <div style={{
                padding: "12px 16px",
                background: "#f0fdf4", borderBottom: "1px solid #86efac",
                fontWeight: 700, fontSize: "14px", color: "#166534",
              }}>
                ✅ Entregados hoy ({yaEntregados.length})
              </div>
              <div style={{ padding: "10px 12px 12px", display: "flex", flexDirection: "column", gap: "6px" }}>
                {yaEntregados.map(p => (
                  <div key={p.id} style={{
                    background: "#f0fdf4", border: "1.5px solid #86efac",
                    borderRadius: "10px", padding: "11px 14px",
                    display: "flex", justifyContent: "space-between", alignItems: "center",
                  }}>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: "13px", color: "#166534" }}>
                        #{p.numero || p.id}
                      </div>
                      <div style={{ fontSize: "12px", color: "#64748b", marginTop: "2px" }}>{p.cliente}</div>
                    </div>
                    <span style={{
                      fontSize: "12px", fontWeight: 700, color: "#166534",
                      background: "#dcfce7", padding: "4px 10px", borderRadius: "20px",
                    }}>
                      ✓ Entregado
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activos.length === 0 && despachados.length === 0 && (
            <div style={{ textAlign: "center", padding: "64px 20px", color: "#94a3b8" }}>
              <div style={{ fontSize: "48px", marginBottom: "12px" }}>📭</div>
              <strong style={{ color: "#334155", display: "block", fontSize: "16px", marginBottom: "6px" }}>
                No hay pedidos para retirar
              </strong>
            </div>
          )}
        </div>
      )}

      {/* Modal de escaneo */}
      {scanPedido && (
        <ScanModal
          pedido={scanPedido}
          onClose={() => setScanPedido(null)}
          onEntregado={() => {
            setScanPedido(null);
            showToast("✅ Entrega confirmada");
          }}
        />
      )}
    </div>
  );
}
